import React, { Fragment, FunctionComponent, MouseEvent, useRef, useState } from "react";

import { bemClassNames } from "@multiweb/reactUtils";
import { MouseButtons } from "@multiweb/types/react";
import { SplitPanelLightProps } from "../types";
import { getContainersRatio, getMaxContainerPercentage, getMinContainerPercentage, getNextBlockPercentage, getPreviousBlockPercentage, validateSplitPanelProps } from "../utils";

const classNames = bemClassNames("split-horizontal-panel-light");
const splitHorizontalPanelStyle = classNames();
const draggerClassName = classNames("dragger");

export const SplitHorizontalPanelLight: FunctionComponent<SplitPanelLightProps> = ({ minSizePercentages, defaultPercentages, fullDimensionIndex, children }) => {
    validateSplitPanelProps(children, minSizePercentages, defaultPercentages);

    const [containersHeight, setContainersHeight] = useState<readonly number[]>(defaultPercentages ?? Array(children.length).fill(100 / children.length));
    const isResizing = useRef(false);
    const selectedDraggerIndexRef = useRef(0);
    const minSizePercentage = useRef(0);
    const maxSizePercentage = useRef(0);

    const handleResizeStart = (index: number) => {
        isResizing.current = true;
        selectedDraggerIndexRef.current = index;
        minSizePercentage.current = getMinContainerPercentage(containersHeight, minSizePercentages[index], index);
        maxSizePercentage.current = getMaxContainerPercentage(containersHeight, minSizePercentages[index + 1], index);
    };

    const handleResizeMove = (e: MouseEvent) => {
        if ((e.buttons !== MouseButtons.leftButton && isResizing.current) || !isResizing.current) {
            isResizing.current = false;
            return;
        }

        const { top, height } = e.currentTarget.getBoundingClientRect();
        const draggerIndex = selectedDraggerIndexRef.current;

        const { firstContainerPercentage, secondContainerPercentage } = getContainersRatio(top, height, e.pageY, minSizePercentage.current, maxSizePercentage.current);

        const newContainersHeights = [...containersHeight];
        newContainersHeights[draggerIndex] = getPreviousBlockPercentage(containersHeight, firstContainerPercentage, draggerIndex);
        newContainersHeights[draggerIndex + 1] = getNextBlockPercentage(containersHeight, secondContainerPercentage, draggerIndex);

        setContainersHeight(newContainersHeights);
    };

    const handleResizeStop = () => {
        isResizing.current = false;
    };

    return (
        <div className={splitHorizontalPanelStyle} onMouseMove={handleResizeMove}>
            {children.map((child, i) => (
                (fullDimensionIndex === undefined || fullDimensionIndex === i) && <Fragment key={i}>
                    <div style={{ height: `${fullDimensionIndex !== undefined ? 100 : containersHeight[i]}%` }}>
                        {child}
                    </div>
                    {(i < children.length - 1 && fullDimensionIndex === undefined) && <div className={draggerClassName} onMouseDown={() => handleResizeStart(i)} onMouseUp={handleResizeStop} />}
                </Fragment>
            ))}
        </div>
    );
};
